import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar as MuiAppBar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import ColorModeMenuButton from '@/components/ColorModeMenuButton';
import UserAvatar from '@/components/UserAvatar';
import { useAppLogo } from '@/contexts/LogoProvider';
import { useAuth } from '@/hooks/useAuth';
import { appBarLogoImageSx, appBarLogoTraySx } from '@/utils/loginShellLogo';
import { GLASS_BLUR, GLASS_RADIUS, GLASS_SHADOW, glassBorder } from '@/utils/glassTokens';
import { appBarForeground } from '@/utils/themeSurfaces';

const ROLE_LABELS: Record<string, string> = {
  admin: '管理員',
  developer: '開發者',
  referee: '裁判',
};

type TopBarProps = {
  onMenuClick: () => void;
};

export default function TopBar({ onMenuClick }: TopBarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const logoSrc = useAppLogo();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const displayName = user?.full_name || user?.reference_no || '用戶';
  const roleLabel = user?.role ? ROLE_LABELS[user.role] || user.role : '';

  const isReferee = user?.role !== 'admin' && user?.role !== 'developer';

  const primary = theme.palette.primary.main;
  const appBarColor = appBarForeground(theme, isReferee);
  const primaryRgba = (() => {
    if (isReferee) {
      return isDark ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.92)';
    }
    const hex = primary.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${isDark ? 0.82 : 0.92})`;
  })();

  return (
    <MuiAppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        bgcolor: primaryRgba,
        backdropFilter: GLASS_BLUR,
        WebkitBackdropFilter: GLASS_BLUR,
        borderBottom: glassBorder(isDark),
        boxShadow: GLASS_SHADOW,
        borderBottomLeftRadius: { xs: 0, md: `${GLASS_RADIUS}px` },
        borderBottomRightRadius: { xs: 0, md: `${GLASS_RADIUS}px` },
        color: appBarColor,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 }, gap: 1 }}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="開啟選單"
          onClick={onMenuClick}
          sx={{ display: { md: 'none' }, mr: 0.5, flexShrink: 0 }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          onClick={() => navigate('/dashboard')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
            minWidth: 0,
            flex: 1,
            cursor: 'pointer',
            overflow: 'hidden',
          }}
          aria-label="香港手球裁判分析系統 — 返回總覽"
        >
          <Box
            sx={{
              width: { xs: 36, sm: 44 },
              height: { xs: 36, sm: 44 },
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...appBarLogoTraySx(theme),
            }}
          >
            <Box component="img" src={logoSrc} alt="" sx={appBarLogoImageSx()} />
          </Box>
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontWeight: 700,
              fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
              lineHeight: 1.3,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            香港手球裁判
            <Box
              component="span"
              sx={{
                color: 'secondary.main',
                fontWeight: 800,
                mx: 0.25,
                textShadow: isDark ? '0 0 12px rgba(139,195,74,0.45)' : 'none',
              }}
            >
              分析
            </Box>
            系統
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.75 }, flexShrink: 0 }}>
          {roleLabel && (
            <Chip
              label={roleLabel}
              size="small"
              sx={{
                display: { xs: 'none', sm: 'flex' },
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                bgcolor: isReferee
                  ? 'rgba(255,255,255,0.18)'
                  : isDark
                    ? 'rgba(255,255,255,0.14)'
                    : 'rgba(255,255,255,0.25)',
                color: appBarColor,
              }}
            />
          )}
          <ColorModeMenuButton color="inherit" size="small" sx={{ color: appBarColor }} />

          <Box
            onClick={(e) => setAnchorEl(e.currentTarget as HTMLElement)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              borderRadius: 2,
              px: { xs: 0.5, sm: 1 },
              py: 0.5,
              ml: 0.5,
              '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.12)' },
            }}
            role="button"
            aria-label="用戶選單"
          >
            <Typography variant="body2" noWrap sx={{ display: { xs: 'none', sm: 'block' }, maxWidth: 140, fontWeight: 600 }}>
              {displayName}
            </Typography>
            <UserAvatar
              photoUrl={user?.photo_url}
              photoPath={user?.photo_path}
              referenceNo={user?.reference_no}
              fullName={user?.full_name}
              sx={{
                width: 36,
                height: 36,
                fontSize: '0.95rem',
                border: '2px solid',
                borderColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.85)',
              }}
            />
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center' }}>
            <UserAvatar
              photoUrl={user?.photo_url}
              photoPath={user?.photo_path}
              referenceNo={user?.reference_no}
              fullName={user?.full_name}
              sx={{
                width: 48,
                height: 48,
                border: '2px solid',
                borderColor: 'divider',
              }}
            />
            <Box sx={{ ml: 1.5, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {user?.reference_no}
                {roleLabel ? ` · ${roleLabel}` : ''}
              </Typography>
            </Box>
          </Box>
          <Divider />
          <MenuItem disabled>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            個人資料（主系統）
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              logout();
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            登出
          </MenuItem>
        </Menu>
      </Toolbar>
    </MuiAppBar>
  );
}
